import { useEffect } from "react";
import { CAMPAIGN, COPYRIGHT_LINE } from "@/lib/campaign";

// [[COMPLIANCE TO REVIEW — DRAFT LEGAL PAGES]] Final legal copy is subject to Kinsfolk Mortgage approval.
const PAGES = {
  privacy: {
    title: "Privacy Policy",
    updated: "Last updated: June 2026",
    sections: [
      {
        h: "1. Who We Are",
        p: [
          "This Privacy Policy applies to the Benefit to Keys webinar registration page operated on behalf of Kinsfolk Mortgage LLC (Company NMLS #2610890) and Chaunci Witherspoon, MBA (Individual NMLS #1703351), licensed in Florida. It explains what information is collected through this page, how it is used, and the choices available to you.",
        ],
      },
      {
        h: "2. Information We Collect",
        p: [
          "Information you provide: first name, last name, email address, mobile phone number, state, an optional VA homebuying question, and your consent choice with its timestamp.",
          "Information collected automatically: marketing attribution parameters (such as UTM values, Facebook click ID, and Google click ID), the page URL you registered from, the referring URL, and the timestamp of your first visit to the page. This information helps us understand how visitors find the workshop.",
        ],
      },
      {
        h: "3. How We Use Your Information",
        p: [
          "To confirm your webinar registration and deliver event confirmations and reminders consistent with the consent you provided.",
          "To offer an optional, complimentary VA homebuying strategy session after the workshop, where requested.",
          "To understand which marketing channels bring visitors to the workshop so we can improve our educational outreach.",
          "We do not sell your personal information.",
        ],
      },
      {
        h: "4. Calls and Text Messages (TCPA Consent)",
        p: [
          "If you check the consent box at registration, you agree to receive recurring informational and marketing calls and text messages from Kinsfolk Mortgage and Chaunci Witherspoon, MBA at the number provided, including through automated technology. Consent is not a condition of purchase. Message and data rates may apply, and message frequency varies.",
          "You may opt out at any time by replying STOP to any text message, and you can reply HELP for help. Opting out of marketing messages does not affect transactional communications you have specifically requested.",
        ],
      },
      {
        h: "5. Sharing of Information",
        p: [
          "Registration information is stored in our customer relationship management system and is accessible to Kinsfolk Mortgage personnel who administer the workshop and follow-up communications. We may share information with service providers who help us operate this page and deliver communications, and where required by law. We do not share your information with unaffiliated third parties for their own marketing purposes.",
        ],
      },
      {
        h: "6. Data Retention and Security",
        p: [
          "We retain registration records for as long as needed to administer the workshop, honor your communication preferences, and meet legal and regulatory obligations. We use commercially reasonable safeguards to protect the information submitted through this page.",
        ],
      },
      {
        h: "7. Your Choices and Rights",
        p: [
          "You may request access to, correction of, or deletion of your registration information, subject to legal record-keeping requirements, by contacting us using the details below. You may withdraw messaging consent at any time by replying STOP.",
        ],
      },
      {
        h: "8. Contact",
        p: [
          "Kinsfolk Mortgage LLC · Chaunci Witherspoon, MBA",
          "Email: c.witherspoon@kinsfolkmortgage.com · Phone: (813) 710-4602",
          "Website: www.kinsfolkmortgage.com",
        ],
      },
    ],
  },
  terms: {
    title: "Terms & Conditions",
    updated: "Last updated: June 2026",
    sections: [
      {
        h: "1. Acceptance of These Terms",
        p: [
          "By accessing the Benefit to Keys registration page or registering for the workshop, you agree to these Terms & Conditions. If you do not agree, please do not use this page.",
        ],
      },
      {
        h: "2. Educational Purpose Only",
        p: [
          "The workshop is a free educational event. It is not a commitment to lend, and registering for or attending the workshop does not constitute a loan application. No hard credit pull is required to register or attend.",
          "Content presented on this page and during the workshop is general education, not individual financial, legal, tax, or lending advice. Individual guidance requires a separate conversation and, when appropriate, an application and supporting documentation.",
        ],
      },
      {
        h: "3. No Guarantees",
        p: [
          "All loans are subject to application, borrower and property eligibility, credit review, documentation, program guidelines, and underwriting approval. Interest rates, loan terms, payments, costs, and program availability may change and vary by borrower and transaction. Results are not guaranteed.",
          "VA home loan eligibility requires qualifying service or other qualifying status and is subject to verification. VA financing is not offered by the Department of Veterans Affairs itself, and this event is not sponsored by, endorsed by, or affiliated with the U.S. Department of Veterans Affairs or any government agency.",
        ],
      },
      {
        h: "4. Registration and Communications",
        p: [
          "You agree to provide accurate registration information. Event confirmations and reminders are sent according to the consent choices captured at registration. See the Privacy Policy for details on calls and text messages, including how to opt out.",
        ],
      },
      {
        h: "5. Intellectual Property",
        p: [
          "The workshop content, the Benefit-to-Keys Blueprint™, workbooks, planners, and the materials on this page are the property of Kinsfolk Mortgage LLC and its licensors. They are provided for your personal, non-commercial use and may not be reproduced or distributed without written permission.",
        ],
      },
      {
        h: "6. Acceptable Use",
        p: [
          "You agree not to misuse this page, including by submitting false information, attempting to interfere with its operation, or using automated means to access or scrape it.",
        ],
      },
      {
        h: "7. Limitation of Liability",
        p: [
          "To the fullest extent permitted by law, Kinsfolk Mortgage LLC and Chaunci Witherspoon, MBA are not liable for indirect, incidental, or consequential damages arising from your use of this page or attendance at the workshop. Nothing in these terms limits rights you may have under applicable law.",
        ],
      },
      {
        h: "8. Governing Law and Changes",
        p: [
          "These terms are governed by the laws of the State of Florida. We may update these Terms & Conditions from time to time; the version posted on this page applies.",
          "Questions: c.witherspoon@kinsfolkmortgage.com · (813) 710-4602 · www.kinsfolkmortgage.com",
        ],
      },
    ],
  },
};

export default function LegalPage({ page }) {
  const content = PAGES[page];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [page]);

  return (
    <div className="min-h-screen px-5 md:px-10 py-16" data-testid={`legal-page-${page}`}>
      <div className="max-w-3xl mx-auto text-left">
        <a href="/" data-testid="legal-back-link" className="font-mono text-xs tracking-[0.2em] text-[var(--accent)] hover:underline">
          ← BACK TO WORKSHOP
        </a>
        <p className="font-mono text-[10px] tracking-[0.1em] text-[#ffb0b0] mt-10 mb-4">
          [[COMPLIANCE TO REVIEW — DRAFT LEGAL COPY, SUBJECT TO KINSFOLK MORTGAGE APPROVAL]]
        </p>
        <h1 className="font-display font-semibold text-4xl sm:text-5xl text-white mb-3">{content.title}</h1>
        <p className="font-mono text-[11px] tracking-[0.1em] text-[var(--muted)] mb-12">{content.updated}</p>
        <div className="space-y-10">
          {content.sections.map((s) => (
            <section key={s.h}>
              <h2 className="font-display font-medium text-lg md:text-xl text-[var(--accent)] mb-4">{s.h}</h2>
              <div className="space-y-4 text-base leading-relaxed text-[rgba(255,255,255,0.78)]">
                {s.p.map((para, i) => (
                  <p key={i}>{para}</p>
                ))}
              </div>
            </section>
          ))}
        </div>
        <p className="font-mono text-[10px] tracking-[0.08em] text-[rgba(255,255,255,0.45)] mt-14 border-t border-[var(--line)] pt-8">
          {COPYRIGHT_LINE} · {CAMPAIGN.hostLegalCompany} · Company NMLS #{CAMPAIGN.companyNmls}
        </p>
      </div>
    </div>
  );
}
